import { useQuery } from '@tanstack/react-query';
import { getFeed, getHosts, getMyOpenTasks } from '@/api/calendar';
import { useProject } from '@/contexts/ProjectProvider';
import { calendarBadge } from '@/lib/agenda';
import { feedRange } from '@/lib/calendarDates';

/** Ключи кэша календаря; всё под ['crm','cal',проект] — живые события перечитывают разом. */
export const calKeys = {
  all: (p: string) => ['crm', 'cal', p] as const,
  feed: (p: string, staff: string, from: string, to: string) => ['crm', 'cal', p, 'feed', staff, from, to] as const,
  mine: (p: string) => ['crm', 'cal', p, 'mine'] as const,
  past: (p: string, today: string) => ['crm', 'cal', p, 'past', today] as const,
  hosts: (p: string) => ['crm', 'cal', p, 'hosts'] as const,
  quick: (p: string) => ['crm', 'cal', p, 'quick'] as const,
};

/** Выдача календаря на видимые дни (неделя или сетка месяца ≤ 42 дней — в предел 62 укладывается). */
export function useFeed(days: string[], staff: string) {
  const { status, projectId } = useProject();
  const { from, to } = feedRange(days);
  return useQuery({
    queryKey: calKeys.feed(projectId, staff, from, to),
    enabled: status === 'ready' && !!projectId && days.length > 0,
    queryFn: () => getFeed(from, to, staff),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

/** Мои открытые задачи по сегодня (просрочено + сегодня) — блок «Просрочено» и значок вкладки. */
export function useMyOpenTasks() {
  const { status, projectId } = useProject();
  return useQuery({
    queryKey: calKeys.mine(projectId),
    enabled: status === 'ready' && !!projectId,
    queryFn: getMyOpenTasks,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Значок «Календаря»: просроченные + сегодняшние открытые (мои). */
export function useCalendarBadge(): number {
  return calendarBadge(useMyOpenTasks().data);
}

/** Люди, чьи календари можно открыть (только руководителю; иначе не спрашиваем). */
export function useHosts(enabled: boolean) {
  const { status, projectId } = useProject();
  return useQuery({
    queryKey: calKeys.hosts(projectId),
    enabled: enabled && status === 'ready' && !!projectId,
    queryFn: getHosts,
    staleTime: 10 * 60_000,
  });
}
